import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import RefreshButton from "../../components/admin/RefreshButton";
import Spinner from "../../components/ui/Spinner";
import { useAdminToast } from "../../context/AdminToastContext";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { parseApiError } from "../../lib/http/apiErrors";
import { formatISTDateTime } from "../../utils/dateUtils";
import {
  legalAdminService,
  type GatedAction,
  type LegalAcceptance,
  type LegalAcceptanceReport,
  type LegalDocument,
  type LegalDocumentDiff,
  type LegalDocumentStatus,
  type LegalDiffRow,
  type UserAcceptanceHistory,
} from "../../features/legal-admin/service";

/** Document types seeded at launch. `type` is a free-form string on the
 * backend (see the legal schemas module docstring) so a new document class can
 * be introduced from here — the form takes a free-typed value too. */
const DOCUMENT_TYPES: { value: string; label: string }[] = [
  { value: "terms", label: "Terms & Conditions" },
  { value: "privacy_policy", label: "Privacy Policy" },
];

const STATUSES: LegalDocumentStatus[] = ["draft", "published", "archived"];

const STATUS_BADGE: Record<LegalDocumentStatus, string> = {
  draft: "bg-gray-800 text-gray-400",
  published: "bg-green-900/40 text-green-400",
  archived: "bg-amber-900/40 text-amber-400",
};

const typeLabel = (type: string) => DOCUMENT_TYPES.find((t) => t.value === type)?.label ?? type;

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Best-effort "next version" suggestion for the New Draft form — bumps the
 * minor number of an `X.Y` version (`"1.0"` → `"1.1"`). Anything that isn't
 * that exact shape (a one-off scheme like `"2026-09"`) is left for the admin
 * to type themselves rather than guessed at. */
function suggestNextVersion(current: string): string {
  const match = current.trim().match(/^(\d+)\.(\d+)$/);
  if (!match) return "";
  return `${match[1]}.${Number(match[2]) + 1}`;
}

/** Content is admin-authored (this form, or the seed script's conversion of
 * terms.tsx/privacy.tsx) — trusted the same way the public /terms and /privacy
 * pages trust it — but still stripped of anything that could execute script,
 * matching the same rule the actual acceptance screen renders it with
 * (web-frontend/src/features/legal/LegalAcceptancePage.tsx's sanitizeLegalHtml). */
function sanitizeLegalHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/ on[a-z]+="[^"]*"/gi, "")
    .replace(/ on[a-z]+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}

type PreviewTarget = { title: string; version: string; content: string };

/** Renders a changed paragraph's word-level diff as highlighted spans —
 * admin-only "Compare versions" view, never shown to end users. */
function renderWordDiff(segments: LegalDiffRow["word_diff"]) {
  if (!segments) return null;
  return segments.map((seg, i) => {
    if (seg.type === "equal") return <span key={i}>{seg.text}</span>;
    if (seg.type === "added") {
      return (
        <span key={i} className="bg-green-900/50 text-green-300">
          {seg.text}
        </span>
      );
    }
    return (
      <span key={i} className="bg-red-900/50 text-red-300 line-through">
        {seg.text}
      </span>
    );
  });
}

const PAGE_TABS = ["documents", "enforcement", "users"] as const;
type PageTab = (typeof PAGE_TABS)[number];
const PAGE_TAB_LABEL: Record<PageTab, string> = {
  documents: "Documents",
  enforcement: "Enforcement",
  users: "User Lookup",
};

/** Legal versions are a handful of documents per type, so the whole list is
 * fetched in one page and the type/status filters go to the server. */
const DOCUMENTS_LIMIT = 200;
const ACCEPTANCES_PAGE_SIZE = 25;

const emptyForm = () => ({
  type: DOCUMENT_TYPES[0].value,
  version: "",
  title: "",
  content: "",
  locale: "",
  country: "",
  /** `<input type="date">` values — sent as ISO instants on submit. */
  effective_at: "",
  /** Blank = no grace period, immediate enforcement once published
   *  (unchanged default behavior). */
  reacceptance_deadline: "",
  change_summary: "",
  /** Structured "what changed" entries shown in the user-facing What's
   *  Changed view before the mandatory acceptance screen. Optional. */
  changes: [] as { section: string; summary: string }[],
  requires_reacceptance: true,
});

type Form = ReturnType<typeof emptyForm>;

/** This app's one canonical display timezone for admin dates (matches
 *  `formatISTDateTime`/backend's `to_ist_iso_for_json`) — IST has no DST, so
 *  it's always exactly +5:30 from UTC, safe as a constant offset with no
 *  timezone library needed. */
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/** `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` with no timezone
 *  suffix — deliberately rendered as IST wall-clock time, NOT the browser's
 *  own local timezone. Real bug this fixes: an earlier version used plain
 *  `Date` getters (`getHours()` etc.), which read the BROWSER's local
 *  timezone — on a browser/OS set to UTC, that silently showed times 5:30
 *  hours off from what the rest of the admin panel (the Documents table,
 *  every backend-rendered date) assumes is the one true timezone. Using the
 *  UTC getters on a manually IST-shifted timestamp sidesteps the browser's
 *  own timezone entirely, so this is correct regardless of where the admin
 *  physically is or how their machine is configured. */
function toDateTimeLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const ist = new Date(d.getTime() + IST_OFFSET_MS);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
}

/** The inverse of `toDateTimeLocalInput` — takes what the admin typed as IST
 *  wall-clock time and produces the real UTC instant the API expects. */
function fromISTDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const asUtcMs = Date.parse(`${value}:00Z`);
  if (Number.isNaN(asUtcMs)) return null;
  return new Date(asUtcMs - IST_OFFSET_MS).toISOString();
}

/** `now`, formatted the same way, for `min` attributes on the datetime-local
 *  effective/deadline pickers — recomputed fresh on every render rather than
 *  memoized, since "now" is exactly what should keep advancing while the
 *  form stays open. */
function nowForDateTimeLocalMin(): string {
  return toDateTimeLocalInput(new Date().toISOString());
}

const emptyAcceptanceFilters = () => ({
  status: "accepted" as "accepted" | "pending",
  platform: "",
  country: "",
  locale: "",
  date_from: "",
  date_to: "",
  search: "",
});

type AcceptanceFilters = ReturnType<typeof emptyAcceptanceFilters>;

export default function AdminLegalDocsPage() {
  // Three tabs instead of one long stacked page — Documents (list + New
  // Draft form), Enforcement (the gated-actions info panel), and User
  // Lookup (cross-document per-user history) each get their own view.
  const [pageTab, setPageTab] = useState<PageTab>("documents");

  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);

  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<LegalDocumentStatus | "">("");

  // Draft form. `formOpen` with a null `editingId` means "creating a new draft".
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(emptyForm());

  // Lifecycle confirmations
  const [publishTarget, setPublishTarget] = useState<LegalDocument | null>(null);
  /** Testing escape hatch — comma-separated emails/user ids. When set, ONLY
   *  these accounts get emailed/pushed/bell-notified by this publish; every
   *  other active user's status/enforcement is completely unaffected. Blank
   *  (the default) notifies everyone, as normal. */
  const [publishNotifyOnly, setPublishNotifyOnly] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<LegalDocument | null>(null);

  // Acceptance report modal
  const [reportDoc, setReportDoc] = useState<LegalDocument | null>(null);
  const [report, setReport] = useState<LegalAcceptanceReport | null>(null);
  const [acceptances, setAcceptances] = useState<LegalAcceptance[]>([]);
  const [acceptancesTotal, setAcceptancesTotal] = useState(0);
  const [acceptancesPage, setAcceptancesPage] = useState(0);
  const [acceptanceFilters, setAcceptanceFilters] = useState<AcceptanceFilters>(emptyAcceptanceFilters());
  const [reportLoading, setReportLoading] = useState(false);
  useBodyScrollLock(!!reportDoc);

  // Enforcement panel — the fixed, code-level list of gated actions.
  const [gatedActions, setGatedActions] = useState<GatedAction[]>([]);
  const [gatedLoading, setGatedLoading] = useState(true);

  // Per-user acceptance lookup (by email or user id), independent of any one document.
  const [userQuery, setUserQuery] = useState("");
  const [userHistory, setUserHistory] = useState<UserAcceptanceHistory | null>(null);
  const [userSearching, setUserSearching] = useState(false);
  /** Not-found is an expected outcome of a lookup, not a failure — shown inline
   * in the card rather than raised as an error toast. */
  const [userNotFound, setUserNotFound] = useState<string | null>(null);

  // Rendered preview — how a document actually looks on the acceptance
  // screen, not the raw HTML in the Content textarea.
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  useBodyScrollLock(!!previewTarget);

  // Deterministic (no-AI) diff support — see backend/app/modules/legal/diffing.py.
  // `compareBaseline` is the "old" content the form's current content gets
  // diffed against: the source version's content on "New version from…", or
  // the currently-published version of the same type on a plain Edit Draft.
  // Null means "nothing sensible to compare against" (a blank New Draft, or
  // a type with no published version yet) — the compare/auto-fill actions
  // stay disabled in that case rather than diffing against nothing.
  const [compareBaseline, setCompareBaseline] = useState<{ label: string; content: string } | null>(null);
  const [diffResult, setDiffResult] = useState<LegalDocumentDiff | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  useBodyScrollLock(showCompareModal);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await legalAdminService.listDocuments({
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        limit: DOCUMENTS_LIMIT,
      });
      setDocuments(items);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  /** Fixed in code server-side, so it's fetched once on mount and never refetched. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await legalAdminService.getGatedActions();
        if (!cancelled) setGatedActions(items);
      } catch (err) {
        if (!cancelled) setError(parseApiError(err).message);
      } finally {
        if (!cancelled) setGatedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleUserSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const identifier = userQuery.trim();
    if (!identifier) return;
    setUserSearching(true);
    setUserNotFound(null);
    setUserHistory(null);
    try {
      const history = await legalAdminService.getUserAcceptanceHistory(identifier);
      // The API already returns newest-first; re-sorting keeps the table correct
      // regardless, and costs nothing at these sizes.
      setUserHistory({
        ...history,
        acceptances: [...history.acceptances].sort(
          (a, b) => (b.accepted_at ?? "").localeCompare(a.accepted_at ?? ""),
        ),
      });
    } catch (err) {
      const parsed = parseApiError(err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (parsed.code === "LEGAL_USER_NOT_FOUND" || status === 404) {
        setUserNotFound(`No user found for "${identifier}".`);
      } else {
        setError(parsed.message);
      }
    } finally {
      setUserSearching(false);
    }
  };

  /** Every filter — status, platform/country/locale, date range, email/name
   * search — is applied server-side by the backend's `.../acceptances`
   * endpoint; nothing here re-filters a loaded page client-side. Platform/
   * country/locale/date only apply while `status === "accepted"` (a pending
   * user has no acceptance row for them to filter on), so they're sent as
   * `undefined` — not just disabled in the UI — while viewing Pending. */
  const loadReport = useCallback(
    async (doc: LegalDocument, pageIndex: number, filters: AcceptanceFilters) => {
      setReportLoading(true);
      try {
        const isAccepted = filters.status === "accepted";
        const [reportRes, acceptanceRes] = await Promise.all([
          legalAdminService.getAcceptanceReport(doc.id),
          legalAdminService.getAcceptances(doc.id, {
            status: filters.status,
            platform: isAccepted ? filters.platform || undefined : undefined,
            country: isAccepted ? filters.country || undefined : undefined,
            locale: isAccepted ? filters.locale || undefined : undefined,
            acceptedFrom: isAccepted && filters.date_from ? `${filters.date_from}T00:00:00Z` : undefined,
            acceptedTo: isAccepted && filters.date_to ? `${filters.date_to}T23:59:59Z` : undefined,
            search: filters.search || undefined,
            limit: ACCEPTANCES_PAGE_SIZE,
            skip: pageIndex * ACCEPTANCES_PAGE_SIZE,
          }),
        ]);
        setReport(reportRes);
        setAcceptances(acceptanceRes.items);
        setAcceptancesTotal(acceptanceRes.total);
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setReportLoading(false);
      }
    },
    [],
  );

  const handleOpenReport = (doc: LegalDocument) => {
    setReportDoc(doc);
    setReport(null);
    setAcceptances([]);
    setAcceptancesTotal(0);
    setAcceptancesPage(0);
    setAcceptanceFilters(emptyAcceptanceFilters());
    void loadReport(doc, 0, emptyAcceptanceFilters());
  };

  const handleCloseReport = () => {
    setReportDoc(null);
    setReport(null);
    setAcceptances([]);
  };

  const handleAcceptancesPageChange = (next: number) => {
    if (!reportDoc) return;
    setAcceptancesPage(next);
    void loadReport(reportDoc, next, acceptanceFilters);
  };

  /** Any filter change re-queries from page 0 — a stale page index against a
   * newly narrowed result set would show an empty/wrong page. */
  const handleFiltersChange = (next: AcceptanceFilters) => {
    setAcceptanceFilters(next);
    if (!reportDoc) return;
    setAcceptancesPage(0);
    void loadReport(reportDoc, 0, next);
  };

  const handleNewDraft = () => {
    setPageTab("documents");
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
    setError(null);
    setCompareBaseline(null);
    setDiffResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** Best-effort baseline for the diff tools: the currently-published version
   * of `type` (excluding `excludeId`, if it happens to already be that
   * version). Never fatal — a lookup failure just leaves compare/auto-fill
   * disabled, it doesn't block editing. */
  const loadCompareBaseline = async (type: string, excludeId?: string) => {
    try {
      const published = await legalAdminService.listDocuments({ type, status: "published", limit: 1 });
      const candidate = published.find((d) => d.id !== excludeId);
      if (!candidate) {
        setCompareBaseline(null);
        return;
      }
      const full = await legalAdminService.getDocument(candidate.id);
      setCompareBaseline({ label: `${typeLabel(full.type)} ${full.version} (current)`, content: full.content ?? "" });
    } catch {
      // Non-fatal — see docstring above.
      setCompareBaseline(null);
    }
  };

  /** Only offered for drafts — published/archived versions are immutable. */
  const handleEditDraft = async (doc: LegalDocument) => {
    setPageTab("documents");
    setEditingId(doc.id);
    setFormOpen(true);
    setError(null);
    setDiffResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSaving("load-draft");
    try {
      // The list payload omits nothing today, but content is the one field
      // worth re-fetching fresh before an edit overwrites it.
      const full = await legalAdminService.getDocument(doc.id);
      setForm({
        type: full.type,
        version: full.version,
        title: full.title,
        content: full.content ?? "",
        locale: full.locale ?? "",
        country: full.country ?? "",
        effective_at: toDateTimeLocalInput(full.effective_at),
        reacceptance_deadline: toDateTimeLocalInput(full.reacceptance_deadline),
        change_summary: full.change_summary ?? "",
        changes: full.changes ?? [],
        requires_reacceptance: full.requires_reacceptance,
      });
      void loadCompareBaseline(full.type, full.id);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  /** Offered on published/archived rows — the admin's actual "start the next
   * version" path. Fetches the source document's real content fresh (never
   * the list payload, which omits it) and opens a blank New Draft form
   * pre-filled from it: same type/title/locale/country, the content to edit
   * from, and a best-effort next-version suggestion. This is a starting
   * point, not a clone — `editingId` stays null, so Create Draft makes a
   * genuinely new document; the source version is never touched. */
  const handleNewVersionFrom = async (doc: LegalDocument) => {
    setPageTab("documents");
    setEditingId(null);
    setFormOpen(true);
    setError(null);
    setDiffResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSaving("load-draft");
    try {
      const full = await legalAdminService.getDocument(doc.id);
      setForm({
        type: full.type,
        version: suggestNextVersion(full.version),
        title: full.title,
        content: full.content ?? "",
        locale: full.locale ?? "",
        country: full.country ?? "",
        effective_at: "",
        // A new version's grace period and change list are new decisions
        // for THIS version, not carried over from the source — left blank
        // for the admin to set fresh, same as effective_at/change_summary.
        reacceptance_deadline: "",
        change_summary: "",
        changes: [],
        requires_reacceptance: true,
      });
      // This is exactly the case the diff tools exist for: content is
      // pre-filled from the source version, change_summary/changes are
      // deliberately blank, and it's easy to edit only the content and
      // forget to fill those back in. `full.content` (the source, before any
      // edits) is the right "old" side — form.content will diverge from it
      // as the admin edits.
      setCompareBaseline({
        label: `${typeLabel(full.type)} ${full.version} (source)`,
        content: full.content ?? "",
      });
      setSuccess(
        `Started a new draft from ${typeLabel(full.type)} ${full.version} — review the version` +
          " number and content before creating it.",
      );
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  /** The Documents list omits `content` (kept light for the table) — fetch the
   * single document fresh so the preview always shows what's actually stored,
   * not a stale/absent copy. */
  const handlePreviewDoc = async (doc: LegalDocument) => {
    setPreviewLoading(true);
    setError(null);
    try {
      const full = await legalAdminService.getDocument(doc.id);
      setPreviewTarget({ title: full.title, version: full.version, content: full.content ?? "" });
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCancelForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
    setCompareBaseline(null);
    setDiffResult(null);
  };

  /** Deterministic (no-AI) diff of the current content against
   * `compareBaseline` — see backend/app/modules/legal/diffing.py. Populates
   * `diffResult` for both the Compare modal and the "Auto-fill changes"
   * action; never touches the form itself until the admin explicitly
   * applies it. */
  const handleRunDiff = async () => {
    if (!compareBaseline) return;
    setDiffLoading(true);
    setError(null);
    try {
      const result = await legalAdminService.diffDocuments(compareBaseline.content, form.content);
      setDiffResult(result);
      setShowCompareModal(true);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setDiffLoading(false);
    }
  };

  /** Replaces the form's "What's Changed" entries with the diff's
   * suggestions — the admin still sees them in the editable list afterward
   * and can revise or delete any of them before Create Draft / Publish. */
  const handleApplyDiffChanges = () => {
    if (!diffResult) return;
    setForm((f) => ({ ...f, changes: diffResult.changes }));
    setShowCompareModal(false);
    setSuccess(
      diffResult.changes.length
        ? `Applied ${diffResult.changes.length} suggested "What's Changed" entr${diffResult.changes.length === 1 ? "y" : "ies"} — review before publishing.`
        : "No differences found to suggest.",
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.type.trim() || !form.version.trim() || !form.title.trim() || !form.content.trim()) {
      setError("Type, version, title and content are required.");
      return;
    }
    if (
      // Both are the same "YYYY-MM-DDTHH:mm" IST-wall-clock shape, so a
      // plain string comparison sorts chronologically — no Date parsing
      // (and no risk of the browser's own timezone sneaking back in).
      form.reacceptance_deadline &&
      form.effective_at &&
      form.reacceptance_deadline <= form.effective_at
    ) {
      setError("Acceptance deadline must be after the effective date.");
      return;
    }
    setSaving("form");
    setError(null);
    try {
      const cleanedChanges = form.changes
        .map((c) => ({ section: c.section.trim(), summary: c.summary.trim() }))
        .filter((c) => c.section && c.summary);
      const shared = {
        title: form.title.trim(),
        content: form.content,
        locale: form.locale.trim() || null,
        country: form.country.trim() || null,
        effective_at: fromISTDateTimeLocal(form.effective_at),
        reacceptance_deadline: fromISTDateTimeLocal(form.reacceptance_deadline),
        change_summary: form.change_summary.trim() || null,
        changes: cleanedChanges.length ? cleanedChanges : null,
        requires_reacceptance: form.requires_reacceptance,
      };
      if (editingId) {
        await legalAdminService.updateDraft(editingId, shared);
        setSuccess(`Updated draft "${shared.title}".`);
      } else {
        await legalAdminService.createDraft({
          ...shared,
          type: form.type.trim(),
          version: form.version.trim(),
        });
        setSuccess(`Created draft "${shared.title}" (${form.version.trim()}).`);
      }
      handleCancelForm();
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handlePublish = async () => {
    if (!publishTarget) return;
    setSaving(publishTarget.id);
    setError(null);
    try {
      const notifyOnly = publishNotifyOnly
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await legalAdminService.publish(publishTarget.id, undefined, notifyOnly);
      setSuccess(
        notifyOnly.length
          ? `Published ${typeLabel(publishTarget.type)} ${publishTarget.version} — notified only ${notifyOnly.length} test recipient(s).`
          : `Published ${typeLabel(publishTarget.type)} ${publishTarget.version}.`,
      );
      setPublishTarget(null);
      setPublishNotifyOnly("");
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setSaving(archiveTarget.id);
    setError(null);
    try {
      await legalAdminService.archive(archiveTarget.id);
      setSuccess(`Archived ${typeLabel(archiveTarget.type)} ${archiveTarget.version}.`);
      setArchiveTarget(null);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  /** Shown next to the Version field while creating (not editing) a draft —
   * so the admin sees what already exists for this type before typing a
   * version number, instead of finding out only when Create Draft rejects a
   * duplicate. Derived from the already-loaded `documents` list, no extra
   * fetch. */
  const existingVersionsForType = formOpen
    ? documents.filter((d) => d.type === form.type)
    : [];

  const inputCls =
    "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50";
  const filterCls =
    "px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/50";

  return (
    <>
      <div>
        <AdminPageHeader
          title="Legal & Compliance"
          subtitle={
            <>
              Versioned Terms &amp; Conditions and Privacy Policy documents. Drafts stay editable;
              once published a version is immutable, and users are re-prompted to accept it if it's
              flagged as requiring re-acceptance.
            </>
          }
          meta={`${documents.length} document${documents.length === 1 ? "" : "s"}`}
          actions={
            <>
              <button
                onClick={handleNewDraft}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium text-white transition-colors"
              >
                + New Draft
              </button>
              <RefreshButton onRefresh={load} />
            </>
          }
        />

        {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
        {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

        {/* One section visible at a time instead of everything stacked on one
            long page — each tab keeps its own state, so switching away and
            back (e.g. to check Enforcement mid-edit) doesn't lose anything. */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-800">
          {PAGE_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setPageTab(t)}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                pageTab === t
                  ? "border-indigo-500 text-white"
                  : "border-transparent text-gray-500 hover:text-gray-300"
              }`}
            >
              {PAGE_TAB_LABEL[t]}
              {t === "enforcement" && (
                <span className="ml-1.5 text-xs text-gray-600">({gatedActions.length})</span>
              )}
              {t === "documents" && (
                <span className="ml-1.5 text-xs text-gray-600">({documents.length})</span>
              )}
            </button>
          ))}
        </div>

        {pageTab === "enforcement" && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">
              Enforcement — currently blocked until acceptance
              <span className="text-gray-500 font-normal ml-1">({gatedActions.length})</span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              These actions are hard-coded to require current legal acceptance; changing them
              requires a code change, not a setting here.
            </p>
          </div>

          {gatedLoading ? (
            <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
          ) : gatedActions.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-500 text-sm">No gated actions reported.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-800">
                  <tr>
                    <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Action</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Endpoint</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {gatedActions.map((a) => (
                    <tr key={`${a.method} ${a.path}`} className="hover:bg-gray-800/20 transition-colors">
                      <td className="px-5 py-3 text-white text-sm font-medium">{a.action}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs font-mono whitespace-nowrap">
                        <span className="text-indigo-300">{a.method}</span> {a.path}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{a.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {pageTab === "documents" && (
        <>
        {formOpen && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
            <h2 className="text-sm font-semibold text-white mb-4">
              {editingId ? "Edit Draft" : "New Draft"}
            </h2>
            {editingId && (
              <p className="text-xs text-gray-500 mb-4">
                Type and version are fixed once a document exists — to change either, create a new
                version instead.
              </p>
            )}
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Type *</label>
                  <input
                    list="legal-document-types"
                    value={form.type}
                    disabled={!!editingId}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                    placeholder="e.g. terms"
                    className={`${inputCls} disabled:opacity-50`}
                  />
                  <datalist id="legal-document-types">
                    {DOCUMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Version *</label>
                  <input
                    value={form.version}
                    disabled={!!editingId}
                    onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
                    placeholder="e.g. 2.0"
                    className={`${inputCls} disabled:opacity-50`}
                  />
                  {!editingId && existingVersionsForType.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Existing {typeLabel(form.type)} versions:{" "}
                      {existingVersionsForType.map((d, i) => (
                        <span key={d.id}>
                          {i > 0 && ", "}
                          <span className="font-mono">{d.version}</span>{" "}
                          <span className={STATUS_BADGE[d.status].includes("green") ? "text-green-400" : "text-gray-600"}>
                            ({d.status})
                          </span>
                        </span>
                      ))}
                      {" — don't reuse one of these."}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Effective date &amp; time <span className="text-gray-600">(IST)</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={form.effective_at}
                    onChange={(e) => {
                      const effective_at = e.target.value;
                      setForm((f) => ({
                        ...f,
                        effective_at,
                        // A deadline that's now at-or-before the (just moved
                        // forward) effective time is no longer valid — clear
                        // it rather than silently leaving a value the form
                        // will reject on submit.
                        reacceptance_deadline:
                          f.reacceptance_deadline && effective_at && f.reacceptance_deadline <= effective_at
                            ? ""
                            : f.reacceptance_deadline,
                      }));
                    }}
                    className={`${inputCls} [color-scheme:dark]`}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Blank = effective immediately on publish. A past date/time backdates it — the
                    document is treated as already in force since then. No lower limit; this only
                    controls WHICH published version counts as current, never when publishing itself
                    is allowed.
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Acceptance deadline <span className="text-gray-600">(IST)</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={form.reacceptance_deadline}
                    min={form.effective_at || nowForDateTimeLocalMin()}
                    onChange={(e) => setForm((f) => ({ ...f, reacceptance_deadline: e.target.value }))}
                    className={`${inputCls} [color-scheme:dark]`}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Blank = no grace period, blocks immediately once published. Must be after the
                    effective date/time above — set one to let users keep using Gullivr normally until
                    then.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Title *</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Terms & Conditions"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Locale</label>
                  <input
                    value={form.locale}
                    onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))}
                    placeholder="e.g. en (blank = all locales)"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Country</label>
                  <input
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                    placeholder="e.g. IN (blank = all countries)"
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Change summary</label>
                <textarea
                  rows={2}
                  value={form.change_summary}
                  onChange={(e) => setForm((f) => ({ ...f, change_summary: e.target.value }))}
                  placeholder="Fallback text if no structured entries below — say what actually changed."
                  className={inputCls}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-gray-400">What's changed (optional)</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={!compareBaseline || diffLoading || !form.content.trim()}
                      onClick={() => void handleRunDiff()}
                      title={
                        compareBaseline
                          ? `Diff current content against ${compareBaseline.label}`
                          : "No previous version to compare against"
                      }
                      className="text-xs font-medium text-indigo-300 hover:text-indigo-200 disabled:opacity-40 disabled:hover:text-indigo-300"
                    >
                      {diffLoading ? "Comparing…" : "Compare & suggest changes"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({ ...f, changes: [...f.changes, { section: "", summary: "" }] }))
                      }
                      className="text-xs font-medium text-indigo-300 hover:text-indigo-200"
                    >
                      + Add entry
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Shown to users in the What's Changed view before they read the full document —
                  admin-written.{" "}
                  {compareBaseline
                    ? `"Compare & suggest changes" can pre-fill this from a deterministic diff against ${compareBaseline.label} — always reviewed/edited here, never auto-published.`
                    : ""}
                </p>
                {form.changes.length > 0 && (
                  <div className="space-y-2">
                    {form.changes.map((c, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <input
                          value={c.section}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              changes: f.changes.map((row, j) => (j === i ? { ...row, section: e.target.value } : row)),
                            }))
                          }
                          placeholder="Section, e.g. Cancellation Policy"
                          className={`${inputCls} sm:w-56`}
                        />
                        <input
                          value={c.summary}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              changes: f.changes.map((row, j) => (j === i ? { ...row, summary: e.target.value } : row)),
                            }))
                          }
                          placeholder="What changed in this section"
                          className={inputCls}
                        />
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, changes: f.changes.filter((_, j) => j !== i) }))}
                          className="px-2 py-2 text-gray-500 hover:text-red-400 text-xs shrink-0"
                          aria-label="Remove entry"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-gray-400">Content * (plain text or HTML)</label>
                  <button
                    type="button"
                    disabled={!form.content.trim()}
                    onClick={() =>
                      setPreviewTarget({
                        title: form.title.trim() || "(untitled)",
                        version: form.version.trim() || "—",
                        content: form.content,
                      })
                    }
                    className="text-xs font-medium text-indigo-300 hover:text-indigo-200 disabled:opacity-40 disabled:hover:text-indigo-300"
                  >
                    Preview how this will look
                  </button>
                </div>
                <textarea
                  rows={16}
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  className={`${inputCls} font-mono text-xs`}
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.requires_reacceptance}
                  onChange={(e) => setForm((f) => ({ ...f, requires_reacceptance: e.target.checked }))}
                  className="w-4 h-4 rounded accent-amber-500"
                />
                Requires re-acceptance (users who accepted an earlier version are prompted again)
              </label>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={saving === "form" || saving === "load-draft"}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {saving === "form" ? "Saving…" : editingId ? "Update Draft" : "Create Draft"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-white whitespace-nowrap">
              Documents <span className="text-gray-500 font-normal ml-1">({documents.length})</span>
            </h2>
            <div className="flex gap-2 flex-wrap">
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={filterCls}>
                <option value="">All types</option>
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as LegalDocumentStatus | "")}
                className={filterCls}
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{titleCase(s)}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
          ) : documents.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-500 text-sm">No legal documents found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-800">
                  <tr>
                    <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Document</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Type</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Version</th>
                    <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Status</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Effective</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Deadline</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Published</th>
                    <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Re-acceptance</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-800/20 transition-colors">
                      <td className="px-5 py-3 text-white text-sm font-medium">
                        {doc.title}
                        {doc.locale || doc.country ? (
                          <span className="ml-2 text-xs text-gray-500 font-normal">
                            {[doc.locale, doc.country].filter(Boolean).join(" · ")}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{typeLabel(doc.type)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs font-mono">{doc.version}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[doc.status]}`}>
                          {titleCase(doc.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{formatISTDateTime(doc.effective_at)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {doc.reacceptance_deadline ? (
                          formatISTDateTime(doc.reacceptance_deadline)
                        ) : (
                          <span className="text-gray-600">Immediate</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{formatISTDateTime(doc.published_at)}</td>
                      <td className="px-4 py-3 text-center">
                        {doc.requires_reacceptance ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400">
                            Required
                          </span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => void handlePreviewDoc(doc)}
                            disabled={previewLoading}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs transition-colors disabled:opacity-50"
                          >
                            Preview
                          </button>
                          {doc.status === "draft" && (
                            <>
                              <button
                                onClick={() => void handleEditDraft(doc)}
                                className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  setPublishTarget(doc);
                                  setPublishNotifyOnly("");
                                }}
                                disabled={saving === doc.id}
                                className="px-3 py-1 bg-green-900/30 hover:bg-green-900/60 text-green-400 rounded-lg text-xs transition-colors disabled:opacity-50"
                              >
                                Publish
                              </button>
                            </>
                          )}
                          {doc.status === "published" && (
                            <button
                              onClick={() => setArchiveTarget(doc)}
                              disabled={saving === doc.id}
                              className="px-3 py-1 bg-amber-900/30 hover:bg-amber-900/60 text-amber-400 rounded-lg text-xs transition-colors disabled:opacity-50"
                            >
                              Archive
                            </button>
                          )}
                          {(doc.status === "published" || doc.status === "archived") && (
                            <button
                              onClick={() => void handleNewVersionFrom(doc)}
                              disabled={saving === "load-draft"}
                              title="Open a new draft pre-filled with this version's content"
                              className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors disabled:opacity-50"
                            >
                              New version from…
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenReport(doc)}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs transition-colors"
                          >
                            Acceptance Report
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {pageTab === "users" && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">User Acceptance History</h2>
            <p className="text-xs text-gray-500 mt-1">
              Look up one user across all legal documents — search by email or user id.
            </p>
          </div>

          <form onSubmit={(e) => void handleUserSearch(e)} className="px-5 py-4 flex gap-2 flex-wrap items-center">
            <input
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Email or user id"
              className={`${inputCls} sm:w-80`}
            />
            <button
              type="submit"
              disabled={userSearching || !userQuery.trim()}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50"
            >
              {userSearching ? "Searching…" : "Search"}
            </button>
          </form>

          {userNotFound && (
            <div className="px-5 pb-5 text-sm text-amber-400">{userNotFound}</div>
          )}

          {userSearching ? (
            <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
          ) : userHistory ? (
            <>
              <div className="px-5 py-3 border-t border-gray-800">
                <p className="text-sm text-white">{userHistory.email || userHistory.name || userHistory.user_id}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[userHistory.name && userHistory.email ? userHistory.name : null, userHistory.user_id]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>

              <div className="px-5 pb-4">
                {userHistory.pending.length === 0 ? (
                  <p className="text-xs text-green-400">
                    Up to date — nothing currently pending for this user.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-amber-400 font-medium">Still owes acceptance of:</span>
                    {userHistory.pending.map((d) => (
                      <span
                        key={d.id}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400"
                      >
                        {typeLabel(d.type)} {d.version}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {userHistory.acceptances.length === 0 ? (
                <div className="px-5 py-8 text-center text-gray-500 text-sm border-t border-gray-800">
                  This user has not accepted any legal document yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-y border-gray-800">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Document</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Version</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Accepted at</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Platform</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Locale</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Country</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">IP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {userHistory.acceptances.map((a) => (
                        <tr key={a.id} className="hover:bg-gray-800/20 transition-colors">
                          <td className="px-5 py-3 text-white text-xs">{typeLabel(a.document_type)}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs font-mono">{a.document_version}</td>
                          <td className="px-4 py-3 text-white text-xs">{formatISTDateTime(a.accepted_at)}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{a.platform || "—"}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{a.locale || "—"}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{a.country || "—"}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs font-mono">{a.ip_address || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>
        )}
      </div>

      {publishTarget && (
        <ConfirmModal
          title="Publish Version"
          description={`${typeLabel(publishTarget.type)} ${publishTarget.version} becomes the version users see and accept.`}
          items={[
            "The document's content and version become immutable",
            publishTarget.requires_reacceptance
              ? "Users who accepted an earlier version will be asked to accept this one"
              : "Users who already accepted an earlier version are not re-prompted",
          ]}
          confirmLabel={publishNotifyOnly.trim() ? "Publish (test recipients only)" : "Publish"}
          confirmClass="bg-green-600 hover:bg-green-500"
          busy={saving === publishTarget.id}
          busyLabel="Publishing…"
          onConfirm={() => void handlePublish()}
          onCancel={() => {
            setPublishTarget(null);
            setPublishNotifyOnly("");
          }}
        >
          <label className="block text-xs text-gray-400 mb-1">
            Test recipients only <span className="text-gray-600">(optional)</span>
          </label>
          <input
            value={publishNotifyOnly}
            onChange={(e) => setPublishNotifyOnly(e.target.value)}
            placeholder="email or user id, comma-separated — leave blank to notify everyone"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
          <p className="mt-1 text-xs text-gray-500">
            When set, ONLY these accounts get emailed/pushed/bell-notified — every other active
            user's status and enforcement are unaffected (they still correctly see this version as
            pending, they just aren't told). Leave blank for a real publish.
          </p>
        </ConfirmModal>
      )}

      {archiveTarget && (
        <ConfirmModal
          title="Archive Version"
          description={`${typeLabel(archiveTarget.type)} ${archiveTarget.version} stops being served as the current version.`}
          items={[
            "Existing acceptance records are kept for audit",
            "Users see the next current published version of this type instead",
          ]}
          confirmLabel="Archive"
          confirmClass="bg-amber-600 hover:bg-amber-500"
          busy={saving === archiveTarget.id}
          busyLabel="Archiving…"
          onConfirm={() => void handleArchive()}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {previewTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setPreviewTarget(null)}
        >
          <div
            className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-800 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-white">
                  Preview — how users will see this
                </h2>
                <p className="text-gray-500 text-xs mt-1">
                  {previewTarget.title} · Version {previewTarget.version} · matches the acceptance
                  screen's layout (headings, lists, tables) — exact fonts/colors depend on the app theme.
                </p>
              </div>
              <button
                onClick={() => setPreviewTarget(null)}
                className="text-gray-500 hover:text-gray-300 text-sm shrink-0"
              >
                Close ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {previewTarget.content.trim() ? (
                <div
                  className="legal-doc-preview text-sm leading-relaxed text-gray-300"
                  dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(previewTarget.content) }}
                />
              ) : (
                <p className="text-sm text-gray-500">No content to preview yet.</p>
              )}
            </div>

            <div className="border-t border-gray-800 px-5 py-3">
              <label className="flex cursor-pointer items-start gap-3 opacity-60">
                <input type="checkbox" disabled className="mt-0.5 accent-indigo-500" />
                <span className="text-sm text-gray-400">
                  I have read and accept the {previewTarget.title}.
                  <span className="ml-2 text-xs text-gray-600">(preview only — not a real checkbox)</span>
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {showCompareModal && diffResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setShowCompareModal(false)}
        >
          <div
            className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-800 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-white">Compare versions</h2>
                <p className="text-gray-500 text-xs mt-1">
                  Deterministic diff, computed locally — no AI involved. Admin-only; never shown to
                  end users. {compareBaseline ? `Against ${compareBaseline.label}.` : ""}
                </p>
              </div>
              <button
                onClick={() => setShowCompareModal(false)}
                className="text-gray-500 hover:text-gray-300 text-sm shrink-0"
              >
                Close ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
              {diffResult.truncated && (
                <div className="rounded-lg border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
                  Substantial rewrite detected — too many paragraphs changed to list individually.
                  Write a real "What's Changed" summary rather than relying on a per-paragraph list.
                </div>
              )}
              {diffResult.rows.every((r) => r.type === "unchanged") ? (
                <p className="text-gray-500 text-xs">No differences found.</p>
              ) : (
                diffResult.rows
                  .filter((r) => r.type !== "unchanged")
                  .map((row, i) => (
                    <div key={i} className="rounded-lg border border-gray-800 px-3 py-2">
                      <div className="text-xs text-gray-500 mb-1">{row.section}</div>
                      {row.type === "added" && (
                        <div className="bg-green-900/30 text-green-300 rounded px-2 py-1">+ {row.new_text}</div>
                      )}
                      {row.type === "removed" && (
                        <div className="bg-red-900/30 text-red-300 rounded px-2 py-1 line-through">
                          − {row.old_text}
                        </div>
                      )}
                      {row.type === "changed" && (
                        <div className="rounded px-2 py-1 leading-relaxed text-gray-200">
                          {renderWordDiff(row.word_diff)}
                        </div>
                      )}
                    </div>
                  ))
              )}
            </div>

            <div className="border-t border-gray-800 px-5 py-3 flex gap-3">
              <button
                type="button"
                disabled={!diffResult.changes.length}
                onClick={handleApplyDiffChanges}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-medium transition-colors disabled:opacity-40 disabled:hover:bg-indigo-600"
              >
                Use {diffResult.changes.length || 0} suggestion{diffResult.changes.length === 1 ? "" : "s"} for
                What's Changed
              </button>
              <button
                type="button"
                onClick={() => setShowCompareModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-xs transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {reportDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={handleCloseReport}>
          <div
            className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-800 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-white">
                  Acceptance Report — {typeLabel(reportDoc.type)} {reportDoc.version}
                </h2>
                <p className="text-gray-500 text-xs mt-1">{reportDoc.title}</p>
              </div>
              <button onClick={handleCloseReport} className="text-gray-500 hover:text-gray-300 text-sm shrink-0">
                Close ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {report && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4">
                  <div className="bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500">Total users</p>
                    <p className="text-lg font-semibold text-white mt-0.5">{report.total_users}</p>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500">Accepted</p>
                    <p className="text-lg font-semibold text-green-400 mt-0.5">{report.accepted_count}</p>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500">Pending</p>
                    <p className="text-lg font-semibold text-amber-400 mt-0.5">{report.pending_count}</p>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500">Acceptance rate</p>
                    <p className="text-lg font-semibold text-white mt-0.5">
                      {(report.acceptance_rate * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              )}

              <div className="px-5 pb-1 flex gap-1">
                {(["accepted", "pending"] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleFiltersChange({ ...acceptanceFilters, status: s })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      acceptanceFilters.status === s
                        ? "bg-brand-600 text-white"
                        : "bg-gray-800 text-gray-400 hover:text-gray-200"
                    }`}
                  >
                    {s === "accepted" ? "Accepted" : "Pending"}
                  </button>
                ))}
              </div>

              <div className="px-5 pb-3 pt-2 flex flex-wrap gap-2 items-center">
                <span className="text-xs text-gray-500 mr-1">Filter (server-side):</span>
                <input
                  value={acceptanceFilters.search}
                  onChange={(e) => handleFiltersChange({ ...acceptanceFilters, search: e.target.value })}
                  placeholder="Email or name"
                  className={`${filterCls} w-36`}
                />
                {acceptanceFilters.status === "accepted" && (
                  <>
                    <input
                      value={acceptanceFilters.platform}
                      onChange={(e) => handleFiltersChange({ ...acceptanceFilters, platform: e.target.value })}
                      placeholder="Platform"
                      className={`${filterCls} w-28`}
                    />
                    <input
                      value={acceptanceFilters.country}
                      onChange={(e) => handleFiltersChange({ ...acceptanceFilters, country: e.target.value })}
                      placeholder="Country"
                      className={`${filterCls} w-24`}
                    />
                    <input
                      value={acceptanceFilters.locale}
                      onChange={(e) => handleFiltersChange({ ...acceptanceFilters, locale: e.target.value })}
                      placeholder="Locale"
                      className={`${filterCls} w-20`}
                    />
                    <input
                      type="date"
                      value={acceptanceFilters.date_from}
                      onChange={(e) => handleFiltersChange({ ...acceptanceFilters, date_from: e.target.value })}
                      className={filterCls}
                    />
                    <input
                      type="date"
                      value={acceptanceFilters.date_to}
                      onChange={(e) => handleFiltersChange({ ...acceptanceFilters, date_to: e.target.value })}
                      className={filterCls}
                    />
                  </>
                )}
                <button
                  onClick={() => handleFiltersChange(emptyAcceptanceFilters())}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs text-gray-300 transition-colors"
                >
                  Reset
                </button>
              </div>

              {reportLoading ? (
                <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
              ) : acceptances.length === 0 ? (
                <div className="px-5 py-8 text-center text-gray-500 text-sm">
                  {acceptanceFilters.status === "accepted"
                    ? "No matching acceptance records."
                    : "No matching users pending acceptance."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-y border-gray-800">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">User</th>
                        {acceptanceFilters.status === "accepted" ? (
                          <>
                            <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Accepted at</th>
                            <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Version</th>
                            <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Platform</th>
                            <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Country</th>
                            <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Locale</th>
                            <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">IP</th>
                          </>
                        ) : (
                          <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Status</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {acceptances.map((a) => (
                        <tr key={a.user_id} className="hover:bg-gray-800/20 transition-colors">
                          <td className="px-5 py-3 text-white text-xs">
                            <div>{a.email || a.name || a.user_id}</div>
                            {a.email && a.name && <div className="text-gray-500">{a.name}</div>}
                          </td>
                          {a.status === "accepted" ? (
                            <>
                              <td className="px-4 py-3 text-white text-xs">{formatISTDateTime(a.accepted_at)}</td>
                              <td className="px-4 py-3 text-gray-400 text-xs font-mono">{a.document_version}</td>
                              <td className="px-4 py-3 text-gray-400 text-xs">{a.platform || "—"}</td>
                              <td className="px-4 py-3 text-gray-400 text-xs">{a.country || "—"}</td>
                              <td className="px-4 py-3 text-gray-400 text-xs">{a.locale || "—"}</td>
                              <td className="px-4 py-3 text-gray-400 text-xs font-mono">{a.ip_address || "—"}</td>
                            </>
                          ) : (
                            <td className="px-4 py-3 text-amber-400 text-xs">Pending</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {acceptancesTotal > ACCEPTANCES_PAGE_SIZE && (
              <div className="border-t border-gray-800">
                <Pagination
                  variant="panel"
                  page={acceptancesPage}
                  pageSize={ACCEPTANCES_PAGE_SIZE}
                  total={acceptancesTotal}
                  onPageChange={handleAcceptancesPageChange}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
